import {
  Children,
  cloneElement,
  isValidElement,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
} from 'react';

import { Popover, PopperProps, TypographyProps, useTheme } from '@mui/material';
import clsx from 'clsx';
import { bindToggle } from 'material-ui-popup-state/core';
import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';

import { pseudoUnique } from '@cast/utils';

import { tokens } from '../../theme/generated/tokens';
import { ConditionalWrapper } from '../../utils/ConditionalWrapper';
import { mergeSx } from '../../utils/styling';
import IconButton, { IconButtonProps } from '../buttons/IconButton';
import * as Icons from '../icons';
import { List, ListProps } from '../lists/List';
import { ListItem, ListItemProps } from '../lists/ListItem';
import { ListItemIcon } from '../lists/ListItemIcon';
import { ListItemText } from '../lists/ListItemText';
import { Tooltip, TooltipProps } from '../tooltip/Tooltip';
import { useTableContext } from './hooks/useTableContext';

export type EntityActionProps = ListItemProps & {
  disruptive?: boolean;
  tooltipProps?: Omit<TooltipProps, 'children'>;
  subtext?: string;
  primaryTextProps?: TypographyProps<'span', { component?: 'span' }>;
};
export const EntityAction = ({
  children,
  sx,
  disruptive,
  tooltipProps,
  subtext,
  primaryTextProps,
  ...rest
}: EntityActionProps) => {
  const itemColor = rest.disabled
    ? tokens.theme.disabled.fg
    : disruptive
    ? tokens.theme.fg.action.danger
    : tokens.theme.fg.structural.primary;
  const adornmentColor = rest.disabled
    ? tokens.theme.disabled.fg
    : disruptive
    ? tokens.theme.fg.action.danger
    : tokens.theme.fg.structural.secondary;

  return (
    <ConditionalWrapper
      condition={Boolean(tooltipProps)}
      wrapper={(child: ReactNode) => (
        <Tooltip {...tooltipProps!}>
          <div>{child}</div>
        </Tooltip>
      )}
    >
      <ListItem
        sx={mergeSx(
          {
            display: 'flex',
            gap: '4px',
            color: itemColor,
            paddingY: '4px',
          },
          sx
        )}
        {...rest}
        startAdornment={
          rest.startAdornment ? (
            <ListItemIcon color="inherit">{rest.startAdornment}</ListItemIcon>
          ) : null
        }
        startAdornmentSx={mergeSx(
          {
            '&&': {
              color: adornmentColor,
            },
          },
          rest.startAdornmentSx
        )}
      >
        <ListItemText
          primaryTypographyProps={{
            typography: 'P12R',
            color: 'inherit',
            sx: {
              display: 'flex',
              flexDirection: 'row',
            },
            ...primaryTextProps,
          }}
          secondary={subtext}
        >
          {children}
        </ListItemText>
      </ListItem>
    </ConditionalWrapper>
  );
};

export type EntityActionsProps = PropsWithChildren<{
  triggerButtonProps?: IconButtonProps;
  listProps?: ListProps;
  icon?: ReactElement;
  container?: PopperProps['container'];
  stopClickEventBubbling?: boolean;
  testId?: string;
  onOpenChange?: (v: boolean) => void;
}>;

export const EntityActions = ({
  triggerButtonProps,
  listProps,
  children,
  icon,
  container,
  stopClickEventBubbling = true,
  testId = 'entity-actions',
  onOpenChange,
}: EntityActionsProps) => {
  const popupId = useMemo(pseudoUnique, []);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `${popupId}-actions`,
  });
  const theme = useTheme();

  useEffect(() => {
    onOpenChange?.(popupState.isOpen);
  }, [popupState.isOpen, onOpenChange]);

  const isInTable = !!useTableContext();

  if (!icon) {
    const Icon = isInTable ? Icons.DotsThree : Icons.DotsThreeVertical;
    icon = <Icon size={16} className="EntityActions-toggleIcon" />;
  }

  let toggleIcon = icon;

  if (triggerButtonProps?.children) {
    if (isValidElement(triggerButtonProps.children)) {
      toggleIcon = cloneElement(triggerButtonProps.children, {
        className: clsx(
          triggerButtonProps?.className,
          'EntityActions-toggleIcon'
        ),
      } as IconButtonProps);
    }
  }

  const _children = Children.map(children, (child) => {
    if (isValidElement<ListItemProps>(child)) {
      return cloneElement(child, {
        ...child.props,
        onClick: (event: MouseEvent<HTMLLIElement>) => {
          popupState.close();
          child.props.onClick?.(event);
        },
      });
    }
    return child;
  });

  const buttonProps = bindToggle(popupState);
  const popoverProps = bindPopover(popupState);

  return (
    <>
      <IconButton
        data-testid={testId}
        {...buttonProps}
        className={clsx(
          triggerButtonProps?.className,
          'EntityActions-triggerButton',
          isInTable && 'EntityActions-tableActions'
        )}
        onClick={(event: MouseEvent) => {
          if (stopClickEventBubbling) {
            event.stopPropagation();
          }
          buttonProps.onClick(event);
        }}
        variant="ghost"
        size="sm"
        {...triggerButtonProps}
        sx={mergeSx(
          {
            padding: 0,
            ...(popupState.isOpen && {
              backgroundColor: tokens.theme.bg.action.ghostHover,
              boxShadow: `inset 0 0 0 1px ${tokens.theme.border.action.ghostHover}`,

              '.EntityActions-toggleIcon': {
                color: tokens.theme.fg.selection.primaryHover,
              },
            }),
          },
          triggerButtonProps?.sx
        )}
      >
        {toggleIcon}
      </IconButton>

      <Popover
        {...popoverProps}
        sx={{
          mt: 4,
          zIndex: theme.zIndex.drawer + 1,
          '& ul li:not(:first-of-type)': {
            borderTop: `1px solid ${tokens.theme.border.structural.neutralSubtle}`,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        container={container}
        onClick={
          stopClickEventBubbling
            ? (event) => {
                event.stopPropagation();
              }
            : undefined
        }
        disableScrollLock
      >
        <List
          size="small"
          {...listProps}
          sx={mergeSx({ minWidth: 109 }, listProps?.sx)}
          testId={`${testId}-list`}
        >
          {_children}
        </List>
      </Popover>
    </>
  );
};
